import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "common/components/ui/Drawer";
import { TEMPLATE_TYPES } from "common/constants";
import { trackMetaPixel, typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { TemplateType } from "common/types";

import TemplateTypeIcon from "./TemplateTypeIcon";

const META_PIXEL_EVENT: Record<TemplateType, string> = {
  NOTE_TEMPLATE: "ConnectionNote",
  MESSAGE_TEMPLATE: "DirectMessage",
  INMAIL_TEMPLATE: "InMail",
};

export default function SelectTemplateType({
  isOpen,
  setIsOpen,
  templateTypeFilter,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  templateTypeFilter: TemplateType;
}) {
  const { t } = useTranslation("enums");
  const { search } = useLocation();
  const navigate = useNavigate();
  const isDesktop = useTwBreakpoint("lg");
  const [templateType, setTemplateType] =
    useState<TemplateType>(templateTypeFilter);

  useEffect(() => {
    setTemplateType(templateTypeFilter);
  }, [templateTypeFilter]);

  function reset() {
    setTemplateType(templateTypeFilter);
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Content className="lg:w-[448px]">
        <Title className="mb-8">Choose template type</Title>

        <div className="flex flex-col gap-y-3">
          {typedObjectEntries(TEMPLATE_TYPES).map(([key, { label }]) => {
            const isSelected = key === templateType;

            return (
              <button
                className={clsx(
                  "group flex gap-x-2 rounded-3xl border p-3 transition-colors",
                  isSelected
                    ? "border-purple-500 bg-purple-50"
                    : "border-black-300 hover:bg-black-100",
                )}
                type="button"
                key={key}
                onClick={() => setTemplateType(key)}
                data-cy={key}
              >
                <TemplateTypeIcon type={key} isSelected={isSelected} />

                <div className="flex flex-col gap-y-0.5">
                  <span className="text-left text-sm-bold">{label}</span>
                  <p className="text-left text-xs-medium text-black-500">
                    {t(`templateDescription.${key}`, {
                      ns: "templates",
                      templateType: key,
                    })}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          className="mt-8 ml-auto"
          onClick={() => {
            trackMetaPixel("trackCustom", META_PIXEL_EVENT[templateType]);
            setIsOpen(false);
            navigate({
              pathname: `../new/${templateType.toLowerCase()}`,
              search,
            });
          }}
        >
          Next
        </Button>
      </Content>
    </Component>
  );
}
